import { useState } from "react";

export default function CoverageMap({ className = "" }) {
  const [activeRegion, setActiveRegion] = useState(null);

  const regions = {
    sonderjylland: {
      id: "sonderjylland",
      title: "Kruså / Padborg & omegn",
      subtitle: "Sønderjylland",
      clinic: {
        name: "Kollund Klinik",
        address: "Fjordvænget 9, Kollund",
        zipCity: "6340 Kruså",
        phone: "+45 41 83 65 30"
      },
      feeLocal: "75,-",
      feeLocalText: "Kruså, Padborg & nærområde",
      feeOuter: "100,-",
      feeOuterText: "Gråsten & øvrige områder",
      coveredTowns: [
        "6340 Kruså",
        "6330 Padborg",
        "6300 Gråsten",
        "6310 Broager",
        "6320 Egernsund",
        "6360 Tinglev",
        "6200 Aabenraa"
      ]
    },
    sydfyn: {
      id: "sydfyn",
      title: "Faaborg / Millinge & omegn",
      subtitle: "Sydfyn",
      clinic: {
        name: "Dyreborg Klinik",
        address: "Knoldsborgvej 6, Dyreborg",
        zipCity: "5600 Faaborg",
        phone: "+45 41 83 65 30"
      },
      feeLocal: "75,-",
      feeLocalText: "Faaborg, Millinge & nærområde",
      feeOuter: "100,-",
      feeOuterText: "Øvrige områder på Sydfyn",
      coveredTowns: [
        "5600 Faaborg",
        "5642 Millinge",
        "5600 Dyreborg",
        "Samt omegn på Sydfyn"
      ]
    }
  };

  const highlightRegion = (id) => {
    setActiveRegion(id);
    // Smooth scroll to the unified box if on mobile
    const element = document.getElementById("unified-coverage-box");
    if (element && window.innerWidth < 1024) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setTimeout(() => {
      setActiveRegion(null);
    }, 2500);
  };

  return (
    <section className={`py-12 bg-[#24382e] text-stone-100 w-full ${className}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Denmark Map Column */}
          <div className="lg:col-span-4 bg-white p-4 rounded-2xl border border-brand-medium/10 shadow-sm flex flex-col items-center justify-center relative overflow-hidden">

            {/* Denmark Map Image with Interactive Hotspots */}
            <div className="relative w-full max-w-[240px] mt-6 mb-2 mx-auto">
              <img
                src="https://assets.auxodev.dk/assets/aec437a7-dbd3-431a-ae4e-b42ebba93fa1/dk-kort.png"
                alt="Danmarkskort med dækningsområder"
                className="w-full h-auto object-contain rounded-xl select-none mix-blend-multiply"
              />

              {/* Hotspot 1: Sydjylland (Kollund / Padborg) */}
              <div
                className="absolute cursor-pointer group/marker transition-all duration-300 z-20"
                style={{ top: "82%", left: "24%" }}
                onClick={() => highlightRegion("sonderjylland")}
              >
                {/* Pulse Ring */}
                <span className="absolute -inset-3 rounded-full bg-brand-medium/40 animate-ping" />
                
                {/* Marker Body */}
                <div className={`relative flex items-center justify-center w-8 h-8 rounded-full shadow-md border-2 transition-all duration-300 ${
                  activeRegion === "sonderjylland"
                    ? "bg-brand-medium border-white scale-110 ring-4 ring-brand-medium/20"
                    : "bg-white border-brand-medium hover:bg-brand-light hover:scale-105"
                }`}>
                  <i className={`fa-solid fa-house-medical text-xs transition-colors ${
                    activeRegion === "sonderjylland" ? "text-white" : "text-brand-medium"
                  }`} />
                </div>

                {/* Tooltip/Label */}
                <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-2 py-1 rounded-lg text-[10px] font-bold shadow-md border transition-all duration-300 z-30 ${
                  activeRegion === "sonderjylland"
                    ? "bg-brand-dark text-white border-brand-medium scale-105"
                    : "bg-brand-dark text-white border-brand-dark/50 group-hover/marker:bg-brand-medium group-hover/marker:border-brand-medium"
                }`}>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span>Kruså / Padborg & omegn</span>
                  </span>
                </div>
              </div>

              {/* Hotspot 2: Sydfyn (Dyreborg / Faaborg) */}
              <div
                className="absolute cursor-pointer group/marker transition-all duration-300 z-20"
                style={{ top: "74%", left: "41%" }}
                onClick={() => highlightRegion("sydfyn")}
              >
                {/* Pulse Ring */}
                <span className="absolute -inset-3 rounded-full bg-brand-medium/40 animate-ping" />
                
                {/* Marker Body */}
                <div className={`relative flex items-center justify-center w-8 h-8 rounded-full shadow-md border-2 transition-all duration-300 ${
                  activeRegion === "sydfyn"
                    ? "bg-brand-medium border-white scale-110 ring-4 ring-brand-medium/20"
                    : "bg-white border-brand-medium hover:bg-brand-light hover:scale-105"
                }`}>
                  <i className={`fa-solid fa-house-medical text-xs transition-colors ${
                    activeRegion === "sydfyn" ? "text-white" : "text-brand-medium"
                  }`} />
                </div>

                {/* Tooltip/Label */}
                <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-2 py-1 rounded-lg text-[10px] font-bold shadow-md border transition-all duration-300 z-30 ${
                  activeRegion === "sydfyn"
                    ? "bg-brand-dark text-white border-brand-medium scale-105"
                    : "bg-brand-dark text-white border-brand-dark/50 group-hover/marker:bg-brand-medium group-hover/marker:border-brand-medium"
                }`}>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span>Faaborg / Millinge & omegn</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Map Legend */}
            <div className="mt-2 flex flex-col gap-1 text-[10px] text-stone-700 border-t border-stone-300/40 pt-2.5 w-full text-center">
              <div className="flex items-center justify-center gap-1.5">
                <i className="fa-solid fa-house-medical text-brand-medium" />
                <span className="font-bold text-brand-dark">Kruså/Padborg & Faaborg/Millinge</span>
              </div>
              <div className="text-stone-600">Fysiske klinikker & udekørende behandling</div>
            </div>
          </div>

          {/* Unified Detail Box Column */}
          <div className="lg:col-span-8 flex flex-col justify-between">
            <div
              id="unified-coverage-box"
              className="bg-white text-brand-dark rounded-2xl border border-stone-200/60 shadow-lg p-5 sm:p-6 flex flex-col justify-between h-full"
            >
              {/* Box Header */}
              <div className="border-b border-stone-300/40 pb-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-brand-dark rounded-lg text-brand-cream">
                    <i className="fa-solid fa-map-location-dot text-sm" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-brand-dark">Klinikker & Dækningsområder</h3>
                    <p className="text-[11px] text-stone-600">Fysiske klinikker og udekørende fodpleje i Kruså/Padborg & omegn samt Faaborg/Millinge & omegn</p>
                  </div>
                </div>
                <span className="text-brand-cream bg-brand-dark font-semibold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-brand-dark/50 self-start sm:self-auto">
                  Geografisk dækning
                </span>
              </div>

              {/* Two Regions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sydjylland Column */}
                <div
                  className={`p-4 rounded-xl border transition-all duration-500 space-y-3 ${
                    activeRegion === "sonderjylland"
                      ? "bg-white border-brand-medium ring-2 ring-brand-medium/10 shadow-md"
                      : "bg-white/60 border-stone-300/30"
                  }`}
                >
                  {/* Region Title */}
                  <div className="flex items-center justify-between border-b border-stone-100 pb-2">
                    <span className="text-sm font-bold text-brand-dark flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-brand-medium" />
                      {regions.sonderjylland.title}
                    </span>
                    <span className="text-xs font-semibold text-brand-medium bg-brand-light/40 px-2 py-0.5 rounded">
                      {regions.sonderjylland.subtitle}
                    </span>
                  </div>

                  {/* Clinic Address */}
                  <div className="bg-white p-3 rounded-lg border border-stone-100 space-y-1">
                    <div className="text-[9px] font-bold text-stone-400 uppercase tracking-wider flex items-center gap-1">
                      <i className="fa-solid fa-house-chimney-medical text-brand-medium" />
                      <span>Fysisk Klinik</span>
                    </div>
                    <div className="text-xs font-bold text-brand-dark">{regions.sonderjylland.clinic.name}</div>
                    <div className="text-[11px] text-stone-600">{regions.sonderjylland.clinic.address}, {regions.sonderjylland.clinic.zipCity}</div>
                  </div>

                  {/* Mobile service coverage */}
                  <div className="bg-brand-light/10 p-3 rounded-lg border border-brand-medium/10 space-y-1.5">
                    <div className="text-[9px] font-bold text-brand-medium uppercase tracking-wider flex items-center gap-1">
                      <i className="fa-solid fa-car-side" />
                      <span>Udekørende Fodpleje</span>
                    </div>
                    <div className="text-[11px] text-stone-700 font-medium">
                      Dækker følgende postnumre:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {regions.sonderjylland.coveredTowns.map((town) => (
                        <span key={town} className="text-[10px] font-semibold bg-white border border-stone-200 text-stone-700 px-1.5 py-0.5 rounded">
                          {town}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sydfyn Column */}
                <div
                  className={`p-4 rounded-xl border transition-all duration-500 space-y-3 ${
                    activeRegion === "sydfyn"
                      ? "bg-white border-brand-medium ring-2 ring-brand-medium/10 shadow-md"
                      : "bg-white/60 border-stone-300/30"
                  }`}
                >
                  {/* Region Title */}
                  <div className="flex items-center justify-between border-b border-stone-100 pb-2">
                    <span className="text-sm font-bold text-brand-dark flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-brand-medium" />
                      {regions.sydfyn.title}
                    </span>
                    <span className="text-xs font-semibold text-brand-medium bg-brand-light/40 px-2 py-0.5 rounded">
                      {regions.sydfyn.subtitle}
                    </span>
                  </div>

                  {/* Clinic Address */}
                  <div className="bg-white p-3 rounded-lg border border-stone-100 space-y-1">
                    <div className="text-[9px] font-bold text-stone-400 uppercase tracking-wider flex items-center gap-1">
                      <i className="fa-solid fa-house-chimney-medical text-brand-medium" />
                      <span>Fysisk Klinik</span>
                    </div>
                    <div className="text-xs font-bold text-brand-dark">{regions.sydfyn.clinic.name}</div>
                    <div className="text-[11px] text-stone-600">{regions.sydfyn.clinic.address}, {regions.sydfyn.clinic.zipCity}</div>
                  </div>

                  {/* Mobile service coverage */}
                  <div className="bg-brand-light/10 p-3 rounded-lg border border-brand-medium/10 space-y-1.5">
                    <div className="text-[9px] font-bold text-brand-medium uppercase tracking-wider flex items-center gap-1">
                      <i className="fa-solid fa-car-side" />
                      <span>Udekørende Fodpleje</span>
                    </div>
                    <div className="text-[11px] text-stone-700 font-medium">
                      Dækker følgende postnumre:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {regions.sydfyn.coveredTowns.map((town) => (
                        <span key={town} className="text-[10px] font-semibold bg-white border border-stone-200 text-stone-700 px-1.5 py-0.5 rounded">
                          {town}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shared Travel Fees Section */}
              <div className="mt-4 p-3.5 bg-white/80 rounded-xl border border-stone-300/20 space-y-2">
                <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-car text-brand-medium" />
                  <span>Kørselsgebyr (Udekørende behandling)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
                  <div className="flex justify-between items-center border-b border-stone-200/40 md:border-b-0 pb-1.5 md:pb-0">
                    <span className="text-stone-600">Nærområde (Kruså, Padborg, Faaborg, Millinge, Dyreborg):</span>
                    <span className="font-bold text-brand-dark ml-2 shrink-0">75,-</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-stone-600">Øvrige dækkede områder (Gråsten, Broager m.fl.):</span>
                    <span className="font-bold text-brand-dark ml-2 shrink-0">100,-</span>
                  </div>
                </div>
              </div>

              {/* Unified Footer Note */}
              <div className="mt-4 pt-3 border-t border-stone-300/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-[11px] text-stone-600">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-info text-brand-medium shrink-0" />
                    <span>Jeg kører gerne ud til private hjem, plejehjem, ældreboliger og institutioner i begge områder.</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-stone-500">
                    <i className="fa-solid fa-phone text-brand-medium/70 shrink-0 text-[10px]" />
                    <span>Du er altid velkommen til at ringe på <a href="tel:+4541836530" className="font-semibold hover:underline text-brand-dark">+45 41 83 65 30</a> for at høre nærmere om, hvor jeg præcist kører.</span>
                  </div>
                </div>
                <div className="shrink-0">
                  <a
                    href="/kontakt"
                    className="inline-flex items-center gap-2 bg-brand-dark hover:bg-brand-medium text-brand-cream text-xs font-semibold px-4 py-2 rounded-lg transition-colors shadow-xs"
                  >
                    <i className="fa-solid fa-paper-plane" />
                    <span>Kontakt mig</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
